/**
 * 全局轻提示 / 加载中 / 二次确认
 *
 * 统一交给 Element Plus（ElMessage / ElLoading / ElMessageBox）渲染，
 * 不再维护自建的提示队列，样式随主题自动切换。
 * 对外的调用方式保持原样，业务代码不用改。
 */
import { ElLoading, ElMessage, ElMessageBox, type MessageHandler } from 'element-plus'

/** 全屏加载遮罩实例（计数式：多次 show 只关最后一次） */
let loadingInstance: ReturnType<typeof ElLoading.service> | null = null
let loadingCount = 0

export const toast = {
  /** 成功提示 */
  success(text: string, duration = 2000) {
    return ElMessage({ message: text, type: 'success', duration })
  },
  /** 错误提示 */
  error(text: string, duration = 2500) {
    return ElMessage({ message: text, type: 'error', duration, showClose: true })
  },
  /** 普通提示 */
  info(text: string, duration = 2000) {
    return ElMessage({ message: text, type: 'info', duration })
  },
  /** 加载中（duration=0 不自动关闭，需手动 hideLoading） */
  loading(text = '处理中…') {
    return ElMessage({ message: text, duration: 0, icon: undefined })
  },
  /** 关闭加载中提示：传实例关单条，不传则关闭全部 */
  hideLoading(id?: MessageHandler) {
    if (id) id.close()
    else ElMessage.closeAll()
  }
}

/** 显示全屏加载遮罩（全局计数，避免嵌套时提前关闭） */
export function showLoading(text = '处理中…') {
  loadingCount++
  if (loadingCount === 1 && !loadingInstance) {
    loadingInstance = ElLoading.service({ fullscreen: true, text, background: 'rgba(0, 0, 0, 0.2)' })
  }
}

/** 关闭全屏加载遮罩 */
export function hideLoading() {
  loadingCount = Math.max(0, loadingCount - 1)
  if (loadingCount === 0 && loadingInstance) {
    loadingInstance.close()
    loadingInstance = null
  }
}

/**
 * 二次确认弹窗
 * @returns Promise<boolean> 用户是否点击了确认
 */
export async function showConfirm(options: {
  title?: string
  content: string
  confirmText?: string
  cancelText?: string
  danger?: boolean
}): Promise<boolean> {
  try {
    await ElMessageBox.confirm(options.content, options.title || '提示', {
      confirmButtonText: options.confirmText || '确定',
      cancelButtonText: options.cancelText || '取消',
      type: options.danger ? 'warning' : 'info',
      // 危险操作：确认按钮用主色以外的警示色（Element Plus 的 warning 主题）
      confirmButtonClass: options.danger ? 'el-button--danger' : ''
    })
    return true
  } catch {
    return false
  }
}
